import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const isFetchingRef = useRef(false);
  const mountedRef = useRef(true);

  const fetchProfile = async (userId) => {
    if (!userId) {
      setProfile(null);
      return null;
    }

    // Prevent concurrent fetches
    if (isFetchingRef.current) return null;
    isFetchingRef.current = true;

    try {
      const { data, error } = await Promise.race([
        supabase
          .from('profiles')
          .select('id,role,full_name,phone,avatar_url')
          .eq('id', userId)
          .maybeSingle(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Profile fetch timed out')), 6000)
        ),
      ]);

      if (!mountedRef.current) return null;

      if (error) {
        console.error('Error fetching profile:', error.message);
        // Don't clear profile on transient errors — keep existing
        return null;
      }

      setProfile(data);
      return data;
    } catch (err) {
      console.error('Profile fetch failed:', err.message);
      return null;
    } finally {
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user && mountedRef.current) {
          setUser(session.user);
          await fetchProfile(session.user.id);
        }
      } catch (err) {
        console.error('Auth init error:', err);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mountedRef.current) return;

        // TOKEN_REFRESHED: just update user token silently, don't re-fetch profile
        if (event === 'TOKEN_REFRESHED') {
          if (session?.user) setUser(session.user);
          return;
        }

        // SIGNED_IN: only fetch profile if we don't already have one for this user
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          if (!profile) {
            await fetchProfile(session.user.id);
          }
          if (mountedRef.current) setLoading(false);
          return;
        }

        // SIGNED_OUT
        if (!session) {
          setUser(null);
          setProfile(null);
          if (mountedRef.current) setLoading(false);
          return;
        }
      }
    );

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const refreshProfile = async (userId) => {
    return await fetchProfile(userId || user?.id);
  };

  return (
    <AuthContext.Provider
      value={{ user, profile, loading, signOut: () => supabase.auth.signOut(), refreshProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);